/**
 * Mis vacaciones.
 *
 * El personal reserva sus días sobre un calendario, viendo primero cuántos
 * tiene disponibles. El saldo lo calcula el servidor (la ley: dos semanas por
 * cada cincuenta trabajadas desde el ingreso); acá solo se muestra en palabras
 * simples. La regla dura es el preaviso: nada antes de `rules.earliest_start`
 * (dos semanas desde hoy) — esos días salen bloqueados y no se pueden tocar.
 *
 * El conteo de días hábiles se calcula también en el cliente (L–V menos
 * feriados) para que la persona vea el número antes de confirmar, pero la
 * autoridad es el servidor: el `days` que devuelve el POST es el que vale.
 */

import { useMemo, useState } from 'react';
import {
  dateLabel, periodLabel, today,
  VACATION_STATUS_LABEL, VACATION_STATUS_TONE, type VacationStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { Async, Badge, Button, Field, Modal, Note } from '@/components/ui';
import { Icon } from '@/components/common/NavIcons';
import '@/styles/vacaciones.css';

/* ── Tipos del contrato con el Worker ─────────────────────────────────────── */

interface VacationRequest {
  id: string; start_date: string; end_date: string; days: number;
  status: VacationStatus;
  notes: string | null; rejection_reason: string | null; requested_at: string;
}

interface VacationsData {
  balance: {
    accrued_days: number; used_days: number; pending_days: number;
    available_days: number; hire_date: string; weeks_worked: number;
  };
  requests: VacationRequest[];
  rules: { min_notice_days: number; earliest_start: string; holidays: string[] };
}

/** Los códigos del servidor, dichos como se los diría una persona. */
const ERROR_MESSAGE: Record<string, string> = {
  vacation_too_soon: 'Las vacaciones se piden con al menos dos semanas de anticipación. Elegí fechas más adelante.',
  vacation_overlap: 'Esas fechas se cruzan con otra solicitud tuya. Revisá el calendario.',
  vacation_no_balance: 'No te alcanzan los días disponibles para ese rango.',
  vacation_empty_range: 'El rango elegido no incluye ningún día hábil.',
  invalid_range: 'Esas fechas no forman un rango válido.',
};

function humanError(err: { code?: string; message: string }): string {
  return (err.code && ERROR_MESSAGE[err.code]) || err.message;
}

/* ── Fechas: todo en ISO `YYYY-MM-DD`, aritmética en UTC ──────────────────────
   Se evita `new Date('YYYY-MM-DD')` sin hora porque el huso local puede correr
   el día. Con `T00:00:00Z` la fecha es exactamente la que dice el string. */

function toUtc(iso: string): Date { return new Date(`${iso}T00:00:00Z`); }
function toIso(d: Date): string { return d.toISOString().slice(0, 10); }

function addDays(iso: string, n: number): string {
  const d = toUtc(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return toIso(d);
}

/** Día de la semana con lunes = 0, como arranca el calendario. */
function dowMon0(iso: string): number { return (toUtc(iso).getUTCDay() + 6) % 7; }

function pad2(n: number): string { return String(n).padStart(2, '0'); }

/**
 * Días hábiles del rango inclusive: L–V menos feriados. Con tope de un año —
 * nadie reserva más, y evita iterar de más si llega un rango absurdo.
 */
function businessDays(start: string, end: string, holidays: Set<string>): number {
  let count = 0;
  let d = start;
  for (let i = 0; i < 366 && d <= end; i++) {
    if (dowMon0(d) < 5 && !holidays.has(d)) count++;
    d = addDays(d, 1);
  }
  return count;
}

/* ── La pantalla ──────────────────────────────────────────────────────────── */

export function EmployeeVacations() {
  const query = useApi<VacationsData>('/employee/vacations');
  return (
    <Async query={query}>
      {(data) => <VacationsView data={data} />}
    </Async>
  );
}

function VacationsView({ data }: { data: VacationsData }) {
  const { balance, requests, rules } = data;
  const now = today();

  // El mes que se está mirando. Arranca en el del primer día reservable, que
  // es donde la persona puede hacer algo — no en un mes lleno de bloqueados.
  const [view, setView] = useState(() => {
    const [y, m] = rules.earliest_start.split('-').map(Number);
    return { y: y!, m: m! };
  });

  // La selección: primer toque fija el inicio, el segundo el fin.
  const [sel, setSel] = useState<{ start: string; end: string | null } | null>(null);
  const [confirming, setConfirming] = useState(false);

  const holidays = useMemo(() => new Set(rules.holidays), [rules.holidays]);

  // Días ya ocupados por solicitudes vivas, para pintarlos y no dejarlos tocar.
  const takenDays = useMemo(() => {
    const map = new Map<string, 'pending' | 'approved'>();
    for (const r of requests) {
      if (r.status !== 'REQUESTED' && r.status !== 'APPROVED') continue;
      let d = r.start_date;
      for (let i = 0; i < 366 && d <= r.end_date; i++) {
        map.set(d, r.status === 'APPROVED' ? 'approved' : 'pending');
        d = addDays(d, 1);
      }
    }
    return map;
  }, [requests]);

  const selDays = sel?.end ? businessDays(sel.start, sel.end, holidays) : 0;

  // El motivo por el que (todavía) no se puede reservar, dicho junto al botón.
  const blockReason = !sel ? null
    : !sel.end ? 'Tocá el último día en el calendario.'
    : selDays === 0 ? 'El rango no incluye ningún día hábil.'
    : selDays > balance.available_days
      ? `Solo tenés ${balance.available_days} ${balance.available_days === 1 ? 'día disponible' : 'días disponibles'}.`
    : null;

  const pick = (iso: string) => {
    setSel((s) => {
      // Sin inicio, o toque antes del inicio: se empieza de nuevo desde ahí.
      if (!s || iso < s.start) return { start: iso, end: null };
      // Rango abierto: este toque lo cierra.
      if (s.end === null) return { start: s.start, end: iso };
      // Rango ya cerrado. La primera versión reiniciaba acá, y en el uso real
      // eso rompía la manera natural de marcar: tocar 21, 22, 23… al tercer
      // toque se borraba todo (reporte del dueño). Un toque DESPUÉS del fin
      // extiende el rango; tocar el mismo fin lo deja igual; y un toque
      // adentro del rango lo ACORTA hasta ahí — que es también la manera de
      // deshacer un día de más sin arrancar de cero.
      if (iso >= s.start && iso !== s.end) return { start: s.start, end: iso };
      return s;
    });
  };

  return (
    <>
      {/*
        En escritorio el calendario va a la izquierda y el saldo + resumen +
        solicitudes a la derecha (`.msec--split`, B4). En el teléfono
        `.pantalla-split` aplana las dos columnas en una sola pila: como el
        saldo ahora vive DESPUÉS del calendario en el DOM (para caer a la
        derecha en el monitor), los `.vac-orden-*` de vacaciones.css reponen
        el orden de siempre — saldo primero, protagonista — solo con CSS.
      */}
      <div className="msec--split pantalla-split">
        <div className="pantalla-split__main">
          <section className="msec vac-orden-calendario">
            <div className="msec__title">Reservar días</div>
            <div className="mcard vac-cal-card">
              <div className="vac-cal__head">
                <Button aria-label="Mes anterior" onClick={() => {
                  setView((v) => (v.m === 1 ? { y: v.y - 1, m: 12 } : { y: v.y, m: v.m - 1 }));
                }}><Icon name="chevron-izq" size={16} /></Button>
                <div className="vac-cal__month">{periodLabel(`${view.y}-${pad2(view.m)}-01`)}</div>
                <Button aria-label="Mes siguiente" onClick={() => {
                  setView((v) => (v.m === 12 ? { y: v.y + 1, m: 1 } : { y: v.y, m: v.m + 1 }));
                }}><Icon name="chevron-der" size={16} /></Button>
              </div>

              <MonthGrid
                y={view.y} m={view.m} todayIso={now}
                earliest={rules.earliest_start} holidays={holidays}
                taken={takenDays} sel={sel} onPick={pick}
              />

              <div className="vac-legend">
                <span><i className="vac-dot vac-dot--blocked" /> Muy pronto</span>
                <span><i className="vac-dot vac-dot--off" /> Finde / feriado</span>
                <span><i className="vac-dot vac-dot--pending" /> Pendiente</span>
                <span><i className="vac-dot vac-dot--approved" /> Aprobado</span>
              </div>

              <p className="muted small" style={{ margin: '8px 0 0' }}>
                Se reserva con dos semanas de anticipación: el primer día posible
                es el {dateLabel(rules.earliest_start)}. Findes y feriados no gastan días.
              </p>
            </div>
          </section>
        </div>

        <div className="pantalla-split__lateral msec--split-lateral">
          {/* ── El saldo, protagonista ─────────────────────────────────── */}
          <section className="msec vac-orden-saldo">
            <div className="msec__title">Mis vacaciones</div>
            <div className="mcard vac-hero">
              <div className="vac-hero__num">{balance.available_days}</div>
              <div className="vac-hero__label">
                {balance.available_days === 1 ? 'día disponible' : 'días disponibles'}
              </div>
              {/* El saldo COMO SE VE, no como se lee: una barra con lo devengado
                  partido en usado, reservado y disponible («podría ser más visual,
                  indicadores de cuánto queda de cada uno» — dueño, 29-ago-2026).
                  Los números van en la leyenda: nada depende solo del color. */}
              {balance.accrued_days > 0 && (
                <div
                  className="vac-barra"
                  role="img"
                  aria-label={`De ${balance.accrued_days} días devengados: ${balance.used_days} usados, ${balance.pending_days} reservados y ${balance.available_days} disponibles`}
                >
                  {balance.used_days > 0 && (
                    <span className="vac-barra__seg vac-barra__seg--usado"
                      style={{ flexGrow: balance.used_days }} />
                  )}
                  {balance.pending_days > 0 && (
                    <span className="vac-barra__seg vac-barra__seg--reservado"
                      style={{ flexGrow: balance.pending_days }} />
                  )}
                  {balance.available_days > 0 && (
                    <span className="vac-barra__seg vac-barra__seg--libre"
                      style={{ flexGrow: balance.available_days }} />
                  )}
                </div>
              )}
              {/* Leyenda unificada con la del calendario (B12): antes
                  `.vac-leyenda` era casi la misma clase que `.vac-legend`
                  con un centrado propio; ahora es un modificador. */}
              <div className="vac-legend vac-legend--center">
                <span><i className="vac-punto vac-punto--usado" /> {balance.used_days} usados</span>
                <span><i className="vac-punto vac-punto--reservado" /> {balance.pending_days} reservados</span>
                <span><i className="vac-punto vac-punto--libre" /> {balance.available_days} libres</span>
                <span className="vac-legend__total">de {balance.accrued_days} devengados</span>
              </div>
              <p className="vac-hero__law">
                Se ganan dos semanas de vacaciones por cada cincuenta trabajadas,
                desde tu ingreso el {dateLabel(balance.hire_date)}.
              </p>
            </div>
          </section>

          {/* El resumen de lo elegido, antes de confirmar nada. */}
          {sel && (
            <section className="msec vac-orden-resumen">
              <div className="mcard vac-summary">
                {sel.end ? (
                  <div className="vac-summary__text">
                    <strong>Del {dateLabel(sel.start)} al {dateLabel(sel.end)}</strong>
                    <span> — {selDays} {selDays === 1 ? 'día hábil' : 'días hábiles'}</span>
                  </div>
                ) : (
                  <div className="vac-summary__text">
                    <strong>Desde el {dateLabel(sel.start)}</strong>
                  </div>
                )}
                {blockReason && <p className="muted small" style={{ margin: '4px 0 0' }}>{blockReason}</p>}
                <div className="vac-summary__actions">
                  <Button onClick={() => setSel(null)}>Borrar</Button>
                  <Button variant="primary" block disabled={blockReason !== null}
                    onClick={() => setConfirming(true)}>
                    Reservar
                  </Button>
                </div>
              </div>
            </section>
          )}

          {/* ── Mis solicitudes ────────────────────────────────────────── */}
          <section className="msec vac-orden-solicitudes">
            <div className="msec__title">Mis solicitudes</div>
            {requests.length === 0 ? (
              <div className="mcard">
                <p className="muted small" style={{ margin: 0 }}>
                  Todavía no reservaste vacaciones. Elegí los días en el calendario.
                </p>
              </div>
            ) : (
              requests.map((r) => <RequestCard key={r.id} request={r} />)
            )}
          </section>
        </div>
      </div>

      {confirming && sel?.end && (
        <ConfirmModal
          start={sel.start} end={sel.end} days={selDays}
          onClose={() => setConfirming(false)}
          onDone={() => { setConfirming(false); setSel(null); }}
        />
      )}
    </>
  );
}

/* ── El mes, celda por celda ──────────────────────────────────────────────── */

const WEEKDAYS = ['L', 'K', 'M', 'J', 'V', 'S', 'D'];

function MonthGrid({ y, m, todayIso, earliest, holidays, taken, sel, onPick }: {
  y: number; m: number; todayIso: string; earliest: string;
  holidays: Set<string>; taken: Map<string, 'pending' | 'approved'>;
  sel: { start: string; end: string | null } | null;
  onPick: (iso: string) => void;
}) {
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lead = dowMon0(`${y}-${pad2(m)}-01`); // huecos antes del día 1

  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => `${y}-${pad2(m)}-${pad2(i + 1)}`),
  ];

  // No es un `grid` de verdad (no hay filas/celdas con rol propio, solo
  // botones sueltos en una rejilla visual): `role="grid"` sin esa estructura
  // es ARIA inválido. `group` describe lo que hay —un conjunto de controles
  // relacionados— sin prometer navegación de flechas que esta grilla no
  // implementa (B9).
  return (
    <div className="vac-cal__grid" role="group" aria-label={periodLabel(`${y}-${pad2(m)}-01`)}>
      {WEEKDAYS.map((w) => (
        <div key={w} className="vac-cal__dow" aria-hidden="true">{w}</div>
      ))}
      {cells.map((iso, i) => {
        if (!iso) return <div key={`x${i}`} className="vac-day vac-day--out" aria-hidden="true" />;

        const blocked = iso < earliest;
        const off = dowMon0(iso) >= 5 || holidays.has(iso);
        const takenAs = taken.get(iso);
        const inSel = sel !== null
          && iso >= sel.start && iso <= (sel.end ?? sel.start);
        const isEdge = sel !== null && (iso === sel.start || iso === sel.end);

        const cls = [
          'vac-day',
          blocked ? 'vac-day--blocked' : '',
          off ? 'vac-day--off' : '',
          takenAs === 'pending' ? 'vac-day--pending' : '',
          takenAs === 'approved' ? 'vac-day--approved' : '',
          inSel ? 'vac-day--sel' : '',
          isEdge ? 'vac-day--edge' : '',
          iso === todayIso ? 'vac-day--today' : '',
        ].filter(Boolean).join(' ');

        return (
          <button
            key={iso}
            type="button"
            className={cls}
            // Bloqueado por preaviso u ocupado por otra solicitud: no hay nada
            // que hacer tocándolo. Un rango que los cruce igual lo frena el
            // servidor (`vacation_overlap`).
            disabled={blocked || takenAs !== undefined}
            aria-pressed={inSel}
            aria-label={dateLabel(iso)}
            onClick={() => onPick(iso)}
          >
            {Number(iso.slice(8))}
          </button>
        );
      })}
    </div>
  );
}

/* ── Confirmar la reserva ─────────────────────────────────────────────────── */

function ConfirmModal({ start, end, days, onClose, onDone }: {
  start: string; end: string; days: number; onClose: () => void; onDone: () => void;
}) {
  const [notes, setNotes] = useState('');

  const mutation = useApiMutation(
    () => api.post<{ id: string; days: number }>('/employee/vacations', {
      start_date: start, end_date: end, notes: notes.trim() || undefined,
    }),
    ['/employee/vacations', '/employee/home'],
    { onSuccess: onDone },
  );

  return (
    <Modal
      title="Reservar vacaciones"
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose}>Volver</Button>
          <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Reservando…' : 'Confirmar reserva'}
          </Button>
        </>
      )}
    >
      {mutation.error && <Note tone="danger">{humanError(mutation.error)}</Note>}
      <p>
        Del <strong>{dateLabel(start)}</strong> al <strong>{dateLabel(end)}</strong> —{' '}
        <strong>{days} {days === 1 ? 'día hábil' : 'días hábiles'}</strong>.
        Queda pendiente hasta que la aprueben.
      </p>
      <Field label="Nota" hint="Opcional: algo que quiera contar de estos días.">
        <input className="input" value={notes} maxLength={300}
          onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}

/* ── Una solicitud en la lista ────────────────────────────────────────────── */

function RequestCard({ request: r }: { request: VacationRequest }) {
  const cancel = useApiMutation(
    () => api.post(`/employee/vacations/${r.id}/cancel`),
    ['/employee/vacations', '/employee/home'],
  );

  return (
    <div className="mcard">
      <div className="mrow" style={{ paddingTop: 0 }}>
        <span>
          <strong>{dateLabel(r.start_date)} – {dateLabel(r.end_date)}</strong>
          <span className="msub">
            {r.days} {r.days === 1 ? 'día hábil' : 'días hábiles'}
          </span>
        </span>
        <Badge tone={VACATION_STATUS_TONE[r.status]}>{VACATION_STATUS_LABEL[r.status]}</Badge>
      </div>
      {r.notes && (
        <div className="mrow" style={{ borderBottom: 0 }}>
          <span className="mrow__l">{r.notes}</span>
        </div>
      )}
      {r.status === 'REJECTED' && r.rejection_reason && (
        <Note tone="danger">{r.rejection_reason}</Note>
      )}
      {r.status === 'REQUESTED' && (
        <>
          {cancel.error && <Note tone="danger">{humanError(cancel.error)}</Note>}
          <Button block disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            {cancel.isPending ? 'Cancelando…' : 'Cancelar solicitud'}
          </Button>
        </>
      )}
    </div>
  );
}
